import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest } from 'rxjs';
import { ApiService, AuditLogListEntry } from '../../../shared/services/api.service';

const PAGE_SIZE = 50;

@Component({
  selector: 'app-user-audit-log',
  templateUrl: './user-audit-log.component.html',
  styleUrls: ['./user-audit-log.component.css']
})
export class UserAuditLogComponent implements OnInit {
  userId = '';
  userEmail: string | null = null;
  entries: AuditLogListEntry[] = [];
  availableActions: string[] = [];
  selectedActionFilters: string[] = [];
  nextCursor: string | null = null;
  loadingInitial = false;
  loadingMore = false;
  detailJsonById = new Map<string, string>();
  detailLoadingIds = new Set<string>();
  detailErrorById = new Map<string, string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, q]) => {
      const id = params.get('userId');
      if (!id) {
        void this.router.navigate(['/admin/users']);
        return;
      }
      const email = q.get('email');
      this.userEmail = email && email.length > 0 ? email : null;
      const prevId = this.userId;
      this.userId = id;
      if (prevId !== id) {
        this.resetAndLoad();
      }
    });
  }

  resetAndLoad(): void {
    this.entries = [];
    this.availableActions = [];
    this.selectedActionFilters = [];
    this.nextCursor = null;
    this.detailJsonById.clear();
    this.detailLoadingIds.clear();
    this.detailErrorById.clear();
    this.loadPage(null);
  }

  loadPage(cursor: string | null): void {
    const isInitial = cursor === null;
    if (isInitial) {
      this.loadingInitial = true;
    } else {
      this.loadingMore = true;
    }
    this.apiService.listUserAuditLogs(this.userId, { limit: PAGE_SIZE, cursor: cursor || undefined }).subscribe({
      next: (res) => {
        this.entries = [...this.entries, ...res.entries];
        this.rebuildAvailableActions();
        this.nextCursor = res.nextCursor;
        this.loadingInitial = false;
        this.loadingMore = false;
      },
      error: (error: unknown) => {
        this.loadingInitial = false;
        this.loadingMore = false;
        const err = error as { error?: { error?: { message?: string } } };
        const msg = err.error?.error?.message || 'Failed to load audit log';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  get filteredEntries(): AuditLogListEntry[] {
    if (!this.selectedActionFilters.length) return this.entries;
    const allowed = new Set(this.selectedActionFilters);
    return this.entries.filter((entry) => allowed.has(entry.action));
  }

  removeActionFilter(action: string): void {
    this.selectedActionFilters = this.selectedActionFilters.filter((a) => a !== action);
  }

  clearActionFilters(): void {
    this.selectedActionFilters = [];
  }

  formatActionLabel(action: string): string {
    return action.replace(/_/g, ' ');
  }

  private rebuildAvailableActions(): void {
    const distinct = Array.from(new Set(this.entries.map((entry) => entry.action).filter(Boolean)));
    distinct.sort((a, b) => a.localeCompare(b));
    this.availableActions = distinct;
    if (this.selectedActionFilters.length > 0) {
      const allowed = new Set(distinct);
      this.selectedActionFilters = this.selectedActionFilters.filter((action) => allowed.has(action));
    }
  }

  loadMore(): void {
    if (!this.nextCursor || this.loadingMore) {
      return;
    }
    this.loadPage(this.nextCursor);
  }

  backToUsers(): void {
    void this.router.navigate(['/admin/users']);
  }

  formatTime(iso: string | null): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString();
  }

  onPanelOpened(entry: AuditLogListEntry): void {
    if (!entry.hasDetails) {
      return;
    }
    if (this.detailJsonById.has(entry.id) || this.detailLoadingIds.has(entry.id)) {
      return;
    }
    this.detailLoadingIds.add(entry.id);
    this.detailErrorById.delete(entry.id);
    this.apiService.getUserAuditLog(this.userId, entry.id).subscribe({
      next: (res) => {
        this.detailJsonById.set(entry.id, JSON.stringify(res.entry, null, 2));
        this.detailLoadingIds.delete(entry.id);
      },
      error: (error: unknown) => {
        this.detailLoadingIds.delete(entry.id);
        const err = error as { error?: { error?: { message?: string } } };
        this.detailErrorById.set(entry.id, err.error?.error?.message || 'Failed to load details');
      }
    });
  }
}
